import React, { Fragment, ReactElement, useEffect, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useSelector } from "react-redux";

import Button from "@mui/material/Button";
import Divider from "@mui/material/Divider";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";

import { cloudboxSettingsElements } from "constants/addons";

import IconSelector from "components/shared/images/IconSelector";
import AppModal from "components/shared/modal/AppModal";
import AntSwitch from "components/shared/switch/AntSwitch";

import { postUpdateCloudboxAccessSettings } from "redux/handlers/addonsHandle";

import { getSelectedCloudboxData } from "redux/selectors/cloudboxSelector";

import { useAppDispatch } from "hooks/reduxHook";

const CloudboxSettings = (): ReactElement => {
 const intl = useIntl();
 const dispatch = useAppDispatch();
 const cloudboxData = useSelector(getSelectedCloudboxData);

 const [samba, setSamba] = useState<boolean>(false);
 const [webdav, setWebdav] = useState<boolean>(false);
 const [external, setExternal] = useState<boolean>(false);
 const [ssh, setSsh] = useState<boolean>(false);
 const [zfs, setZfs] = useState<boolean>(false);
 const [startChange, setStartChange] = useState<boolean>(false);
 const [open, setOpen] = useState<boolean>(false);
 const [loading, setLoading] = useState<boolean>(false);

 useEffect(() => {
  setSamba(cloudboxData?.access_settings.samba_enabled || false);
  setWebdav(cloudboxData?.access_settings.webdav_enabled || false);
  setExternal(cloudboxData?.access_settings.reachable_externally || false);
  setSsh(cloudboxData?.access_settings.ssh_enabled || false);
  setZfs(cloudboxData?.access_settings.zfs_enabled || false);
 }, [cloudboxData]);

 const handleConfirm = async () => {
  setLoading(true);
  await dispatch(
   postUpdateCloudboxAccessSettings(cloudboxData?.id || 0, samba, ssh, webdav, zfs, external)
  );
  setLoading(false);
  handleClose();
 };

 const handleSwitchToggle = (section: string, value: boolean) => {
  switch (section) {
   case "samba":
    setSamba(value);
    break;
   case "webdav":
    setWebdav(value);
    break;
   case "ssh":
    setSsh(value);
    break;
   case "zfs":
    setZfs(value);
    break;
   case "external":
    setExternal(value);
    break;
   default:
    break;
  }
 };

 const handleStartChange = () => setStartChange(true);
 const handleStopChange = () => setStartChange(false);

 const handleOpen = () => setOpen(true);
 const handleClose = () => setOpen(false);

 const RenderSettings = ({ section }: { section: string }): ReactElement => {
  switch (section) {
   case "samba":
    return (
     <Stack direction="row" alignItems="center" pr={2}>
      <AntSwitch
       checked={samba}
       onChange={(event) => handleSwitchToggle("samba", event.target.checked)}
      />
     </Stack>
    );
   case "webdav":
    return (
     <Stack direction="row" alignItems="center" pr={2}>
      <AntSwitch
       checked={webdav}
       onChange={(event) => handleSwitchToggle("webdav", event.target.checked)}
      />
     </Stack>
    );
   case "ssh":
    return (
     <Stack direction="row" alignItems="center" pr={2}>
      <AntSwitch
       checked={ssh}
       onChange={(event) => handleSwitchToggle("ssh", event.target.checked)}
      />
     </Stack>
    );
   case "zfs":
    return (
     <Stack direction="row" alignItems="center" pr={2}>
      <AntSwitch
       checked={zfs}
       onChange={(event) => handleSwitchToggle("zfs", event.target.checked)}
      />
     </Stack>
    );
   case "external":
    return (
     <Stack direction="row" alignItems="center" pr={2}>
      <AntSwitch
       checked={external}
       onChange={(event) => handleSwitchToggle("external", event.target.checked)}
      />
     </Stack>
    );
   default:
    return <></>;
  }
 };

 const RenderCheck = ({ section }: { section: string }) => {
  switch (section) {
   case "samba":
    return (
     <IconSelector
      icon={samba ? "CheckCircleIcon" : "CancelIcon"}
      props={{ color: samba ? "success" : "error" }}
     />
    );
   case "webdav":
    return (
     <IconSelector
      icon={webdav ? "CheckCircleIcon" : "CancelIcon"}
      props={{ color: webdav ? "success" : "error" }}
     />
    );
   case "ssh":
    return (
     <IconSelector
      icon={ssh ? "CheckCircleIcon" : "CancelIcon"}
      props={{ color: ssh ? "success" : "error" }}
     />
    );
   case "zfs":
    return (
     <IconSelector
      icon={zfs ? "CheckCircleIcon" : "CancelIcon"}
      props={{ color: zfs ? "success" : "error" }}
     />
    );
   case "external":
    return (
     <IconSelector
      icon={external ? "CheckCircleIcon" : "CancelIcon"}
      props={{ color: external ? "success" : "error" }}
     />
    );
   default:
    return <></>;
  }
 };

 return (
  <Stack>
   <Stack direction="row" justifyContent="space-between" mb={2}>
    <Stack alignItems="center" justifyContent="flex-start" direction="row">
     <IconSelector icon="ServiceIcon" props={{ fontSize: "medium", color: "kxOrange" }} />
     <Typography variant="unboundedTitle" ml={1} fontSize={16}>
      <FormattedMessage id="addons.cloudbox.settings" />
     </Typography>
    </Stack>
   </Stack>
   {!startChange ? (
    <Stack spacing={2}>
     <Stack>
      {cloudboxSettingsElements.map((element, index) => {
       return (
        <Fragment key={`settings-element-${index}`}>
         <Stack direction={"row"} alignItems="center" py={0.5} justifyContent="space-between">
          <Stack>
           <Typography noWrap variant="subtitle1" px={1} fontWeight="bold">
            <FormattedMessage id={element.text} />
           </Typography>
          </Stack>

          <RenderCheck section={element.section} />
         </Stack>
         {cloudboxSettingsElements.length - 1 !== index && <Divider />}
        </Fragment>
       );
      })}
     </Stack>
     <Stack direction="row" spacing={1} justifyContent="flex-end">
      <Button onClick={handleStartChange}>
       <FormattedMessage id="app.edit" />
      </Button>
     </Stack>
    </Stack>
   ) : (
    <Stack spacing={2}>
     <Stack>
      {cloudboxSettingsElements.map((element, index) => {
       return (
        <Fragment key={`settings-element-${index}`}>
         <Stack direction={"row"} alignItems="center" py={0.5} justifyContent="space-between">
          <Stack>
           <Typography noWrap variant="subtitle1" px={1} fontWeight="bold">
            <FormattedMessage id={element.text} />
           </Typography>
          </Stack>

          <RenderSettings section={element.section} />
         </Stack>
         {cloudboxSettingsElements.length - 1 !== index && <Divider />}
        </Fragment>
       );
      })}
     </Stack>
     <Stack direction="row" spacing={1} justifyContent="flex-end">
      <Button onClick={handleStopChange}>
       <FormattedMessage id="app.cancel" />
      </Button>
      <Button onClick={handleOpen}>
       <FormattedMessage id="app.confirm" />
      </Button>
     </Stack>
    </Stack>
   )}
   <AppModal
    open={open}
    close={handleClose}
    title={intl.formatMessage({ id: "addons.cloudbox.editSettings" })}
    handleClose={handleClose}
    disabled={loading}
    handleConfirm={handleConfirm}>
    <Typography>
     <FormattedMessage id="addons.cloudbox.editSettingsMessage" />
    </Typography>
   </AppModal>
  </Stack>
 );
};

export default CloudboxSettings;
